import { createSlice } from '@reduxjs/toolkit';
import { dmProjectsAction } from './dmProjects.actions';

interface PMProjectsState {
  loading: boolean;
  success: any;
  error: any;
  assignedProjects: any;
  projectInvoicesList: any;
  projectInvoiceDetails: any;
  releaseProjectInvoiceData: any;
  projectOverviewData: any;
  projectTimelineList: any;
  updateTaskConnections: any;
  projectPhaseTasksList: any;
  projectOverviewTaskData: any;
  projectPhases: any;
  projectOverviewAddTask: any;
  projectOverviewUpdateTask: any;
  projectOverviewDeleteTask: any;
  projectChangeOrderList: any;
  projectChangeOrderItems: any;
  createChangeOrder: any,
  changeOrderDetails: any,
  changeOrderTerms: any,
  savedChangeOrderNotes: any,
  changeOrderAttachmentList: any,
  changeOrderUploadSuccess: boolean,
  changeOrderRemoveSuccess: boolean,
  changeOrderRelease: boolean,
  changeOrderDelete: any,
  changeOrderDuplicate: any,
  coPaymentSchedule: any,
  rfpList: any
}
const initialState: PMProjectsState = {
  loading: false,
  success: null,
  error: null,
  assignedProjects: [],
  projectInvoicesList: [],
  projectInvoiceDetails: null,
  releaseProjectInvoiceData: null,
  projectOverviewData: null,
  projectTimelineList: [],
  updateTaskConnections: null,
  projectPhaseTasksList: [],
  projectOverviewTaskData: null,
  projectPhases: [],
  projectOverviewAddTask: null,
  projectOverviewUpdateTask: null,
  projectOverviewDeleteTask: null,
  projectChangeOrderList: [],
  projectChangeOrderItems: [],
  createChangeOrder: null,
  changeOrderDetails: null,
  changeOrderTerms: null,
  savedChangeOrderNotes: null,
  changeOrderAttachmentList: null,
  changeOrderUploadSuccess: false,
  changeOrderRemoveSuccess: false,
  changeOrderRelease: false,
  changeOrderDelete: null,
  changeOrderDuplicate: null,
  coPaymentSchedule: [],
  rfpList: null
};

const dmProjectsSlice = createSlice({
  name: "dmProjectsSlice",
  initialState,
  reducers: {
    cleanChangeOrder: (state) => {
      state.createChangeOrder = [];
    },
    setProjectTimelineList: (state, action) => {
      state.projectTimelineList = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // -------- cases of project manager assigned projects list api data -------
      .addCase(dmProjectsAction.getProjectsListAction.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.success = null;
        state.assignedProjects = [];
      })
      .addCase(dmProjectsAction.getProjectsListAction.fulfilled, (state, action) => {
            state.loading = false;
            if(action?.payload?.data?.status===200){
              state.error = null;
              state.success = action?.payload?.data?.message;
              state.assignedProjects = action?.payload?.data?.data;
            }else{
              state.success = null;
              state.assignedProjects = [];
              state.error = 'Projects list could not be fetched.';
            }
      })
      .addCase(dmProjectsAction.getProjectsListAction.rejected, (state, action:any) => {
            state.loading = false;
            state.success = null;
            state.assignedProjects = [];
            state.error = action?.payload?.data?.message ?? 'Projects list could not be fetched.';
      })

      // -------- cases of project center invoices list api data -------
      .addCase(dmProjectsAction.getProjectInvoicesListAction.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.success = null;
        state.projectInvoicesList = [];
      })
      .addCase(dmProjectsAction.getProjectInvoicesListAction.fulfilled, (state, action) => {
            state.loading = false;
            if(action?.payload?.data?.status===200){
              state.error = null;
              state.success = action?.payload?.data?.message;
              state.projectInvoicesList = action?.payload?.data?.data;
            }else{
              state.success = null;
              state.projectInvoicesList = [];
              state.error = 'Projects invoices could not be fetched.';
            }
      })
      .addCase(dmProjectsAction.getProjectInvoicesListAction.rejected, (state, action:any) => {
            state.loading = false;
            state.success = null;
            state.projectInvoicesList = [];
            state.error = action?.payload?.data?.message ?? 'Projects invoices could not be fetched.';
      })

      // -------- cases of project center invoice details api data -------
      .addCase(dmProjectsAction.getProjectInvoiceDetailsAction.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.success = null;
        state.projectInvoiceDetails = null;
      })
      .addCase(dmProjectsAction.getProjectInvoiceDetailsAction.fulfilled, (state, action) => {
        state.loading = false;
        if(action?.payload?.data?.status===200){
          state.error = null;
          state.success = action?.payload?.data?.message;
          state.projectInvoiceDetails = action?.payload?.data?.data;
        }else{
          state.success = null;
          state.projectInvoiceDetails = null;
          state.error = 'Projects invoice details could not be fetched.';
        }
      })
      .addCase(dmProjectsAction.getProjectInvoiceDetailsAction.rejected, (state, action:any) => {
        state.loading = false;
        state.success = null;
        state.projectInvoiceDetails = null;
        state.error = action?.payload?.data?.message ?? 'Projects invoice details could not be fetched.';
      })

      // -------- cases of project center invoice release api data -------
      .addCase(dmProjectsAction.releaseInvoicesAction.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.success = null;
        state.releaseProjectInvoiceData = null;
      })
      .addCase(dmProjectsAction.releaseInvoicesAction.fulfilled, (state, action) => {
        state.loading = false;
        if(action?.payload?.data?.status===200){
          state.error = null;
          state.success = action?.payload?.data?.message;
          state.releaseProjectInvoiceData = action?.payload?.data?.data;
        }else{
          state.success = null;
          state.releaseProjectInvoiceData = null;
          state.error = 'Projects invoice could not be released.';
        }
      })
      .addCase(dmProjectsAction.releaseInvoicesAction.rejected, (state, action:any) => {
        state.loading = false;
        state.success = null;
        state.releaseProjectInvoiceData = null;
        state.error = action?.payload?.data?.message ?? 'Projects invoice could not be released.';
      })

      // -------- cases of project overview details api data -------
      .addCase(dmProjectsAction.getProjectOverviewDetailsAction.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.success = null;
        // set this as null only when user is not fetching this data while updating project tasks
        if(!action?.meta?.arg?.hideLoader){
          state.projectOverviewData = null;
        }
      })
      .addCase(dmProjectsAction.getProjectOverviewDetailsAction.fulfilled, (state, action) => {
            state.loading = false;
            if(action?.payload?.data?.status===200){
              state.error = null;
              state.success = action?.payload?.data?.message;
              state.projectOverviewData = action?.payload?.data?.data;

              const allOption = {
                  id: "all",
                  name: "All",
              }

              if(action?.meta?.arg?.filters?.assignedTo===undefined){
                // set user list as selected assignedTo array
                state.projectOverviewData = {
                  ...state.projectOverviewData,
                  filters: {
                    assignedTo: [allOption, ...action?.payload?.data?.data?.assignedUsers]
                  },
                  assignedUsers: [allOption, ...action?.payload?.data?.data?.assignedUsers]
                }
              }else{
                const filteredAssignedTo = action?.payload?.data?.data?.assignedUsers.filter((item: any) => action?.meta?.arg?.filters?.assignedTo.includes(item.id));
                // set assignedTo as selected assignedTo array
                state.projectOverviewData = {
                  ...state.projectOverviewData,
                  filters: {
                    assignedTo: filteredAssignedTo?.length === action?.payload?.data?.data?.assignedUsers?.length? [allOption, ...action?.payload?.data?.data?.assignedUsers]: filteredAssignedTo
                  },
                  assignedUsers: [allOption, ...action?.payload?.data?.data?.assignedUsers]
                }
              }
            }else{
              state.success = null;
              state.projectOverviewData = null;
              state.error = 'Projects overview details could not be fetched.';
            }
      })
      .addCase(dmProjectsAction.getProjectOverviewDetailsAction.rejected, (state, action:any) => {
            state.loading = false;
            state.success = null;
            state.projectOverviewData = null;
            state.error = action?.payload?.data?.message ?? 'Projects overview details could not be fetched.';
      })

      // -------- cases of project center invoice update api data -------
      .addCase(dmProjectsAction.updateInvoiceAction.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(dmProjectsAction.updateInvoiceAction.fulfilled, (state, action) => {
        state.loading = false;
        if(action?.payload?.data?.status===200){
          state.error = null;
          state.success = action?.payload?.data?.message;
          state.projectInvoicesList = action?.payload?.data?.data;
        }else{
          state.success = null;
          state.error = 'Projects invoice could not be updated.';
        }
      })
      .addCase(dmProjectsAction.updateInvoiceAction.rejected, (state, action:any) => {
        state.loading = false;
        state.success = null;
        state.releaseProjectInvoiceData = null;
        state.error = action?.payload?.data?.message ?? 'Projects invoice could not be updated.';
      })

      // -------- cases of project timeline list api data -------
      .addCase(dmProjectsAction.getProjectTimelineListAction.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.success = null;
        // set this as null only when user is not fetching this data while updating project tasks
        if(!action?.meta?.arg?.hideLoader){
          state.projectTimelineList = [];
        }
      })
      .addCase(dmProjectsAction.getProjectTimelineListAction.fulfilled, (state, action) => {
            state.loading = false;
            if(action?.payload?.data?.status===200){
              state.error = null;
              state.success = action?.payload?.data?.message;
              state.projectTimelineList = action?.payload?.data?.data?.map((item:any, index:number)=>{
                return {
                  ...item,
                  menuPosition: action?.payload?.data?.data?.length<4 || action?.payload?.data?.data?.length-index>3?true:false,
                }
              });
            }else{
              state.success = null;
              state.projectTimelineList = [];
              state.error = 'Projects timeline list could not be fetched.';
            }
      })
      .addCase(dmProjectsAction.getProjectTimelineListAction.rejected, (state, action:any) => {
            state.loading = false;
            state.success = null;
            state.projectTimelineList = [];
            state.error = action?.payload?.data?.message ?? 'Projects timeline list could not be fetched.';
      })

      // -------- cases of task connections updated api data -------
      .addCase(dmProjectsAction.updateTaskConnectionAction.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(dmProjectsAction.updateTaskConnectionAction.fulfilled, (state, action) => {
        state.loading = false;
        if(action?.payload?.data?.status===200){
          state.error = null;
          state.success = action?.payload?.data?.message;
          state.updateTaskConnections = action?.payload?.data?.data;
        }else{
          state.success = null;
          state.updateTaskConnections = null;
          state.error = 'Task connections could not be updated.';
        }
      })
      .addCase(dmProjectsAction.updateTaskConnectionAction.rejected, (state, action:any) => {
        state.loading = false;
        state.success = null;
        state.updateTaskConnections = null;
        state.error = action?.payload?.data?.message ?? 'Task connections could not be updated.';
      })

      // -------- cases of project phase tasks list api data -------
      .addCase(dmProjectsAction.getProjectTasksListAction.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.success = null;
        // set this as null only when user is not fetching this data while updating project tasks
        if(!action?.meta?.arg?.hideLoader){
          state.projectPhaseTasksList = [];
        }
      })
      .addCase(dmProjectsAction.getProjectTasksListAction.fulfilled, (state, action) => {
            state.loading = false;
            if(action?.payload?.data?.status===200){
              state.error = null;
              state.success = action?.payload?.data?.message;
              state.projectPhaseTasksList = action?.payload?.data?.data?.map((task:any) => {
                return {
                  ...task,
                  label: task?.name
                }
              });
            }else{
              state.success = null;
              state.projectPhaseTasksList = [];
              state.error = 'Projects phase-wise tasks list could not be fetched.';
            }
      })
      .addCase(dmProjectsAction.getProjectTasksListAction.rejected, (state, action:any) => {
            state.loading = false;
            state.success = null;
            state.projectPhaseTasksList = [];
            state.error = action?.payload?.data?.message ?? 'Projects phase-wise tasks list could not be fetched.';
      })

      // -------- cases of project overview task details api data -------
      .addCase(dmProjectsAction.getProjectOverviewTaskDetailsAction.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.success = null;
        if(!action?.meta?.arg?.hideLoader){
          state.projectOverviewTaskData = null;
        }
      })
      .addCase(dmProjectsAction.getProjectOverviewTaskDetailsAction.fulfilled, (state, action) => {
            state.loading = false;
            if(action?.payload?.data?.status===200){
              state.error = null;
              state.success = action?.payload?.data?.message;
              state.projectOverviewTaskData = action?.payload?.data?.data;
            }else{
              state.success = null;
              state.projectOverviewTaskData = null;
              state.error = 'Projects task details could not be fetched.';
            }
      })
      .addCase(dmProjectsAction.getProjectOverviewTaskDetailsAction.rejected, (state, action:any) => {
            state.loading = false;
            state.success = null;
            state.projectOverviewTaskData = null;
            state.error = action?.payload?.data?.message ?? 'Projects task details could not be fetched.';
      })

      // -------- cases of project phases api data -------
      .addCase(dmProjectsAction.getProjectPhasesAction.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.success = null;
        state.projectPhases = [];
      })
      .addCase(dmProjectsAction.getProjectPhasesAction.fulfilled, (state, action) => {
            state.loading = false;
            if(action?.payload?.data?.status===200){
              state.error = null;
              state.success = action?.payload?.data?.message;
              state.projectPhases = action?.payload?.data?.data;
            }else{
              state.success = null;
              state.projectPhases = [];
              state.error = 'Projects phases could not be fetched.';
            }
      })
      .addCase(dmProjectsAction.getProjectPhasesAction.rejected, (state, action:any) => {
            state.loading = false;
            state.success = null;
            state.projectPhases = [];
            state.error = action?.payload?.data?.message ?? 'Projects phases could not be fetched.';
      })

      // -------- cases of add project task api data -------
      .addCase(dmProjectsAction.addProjectTaskAction.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.success = null;
        state.projectOverviewAddTask = null;
      })
      .addCase(dmProjectsAction.addProjectTaskAction.fulfilled, (state, action) => {
            state.loading = false;
            if(action?.payload?.data?.status===200){
              state.error = null;
              state.success = action?.payload?.data?.message;
              state.projectOverviewAddTask = action?.payload?.data?.data;
            }else{
              state.success = null;
              state.projectOverviewAddTask = null;
              state.error = 'Projects task could not be added.';
            }
      })
      .addCase(dmProjectsAction.addProjectTaskAction.rejected, (state, action:any) => {
            state.loading = false;
            state.success = null;
            state.projectOverviewAddTask = null;
            state.error = action?.payload?.data?.message ?? 'Projects task could not be added.';
      })

      // -------- cases of update project task api data -------
      .addCase(dmProjectsAction.updateProjectTaskAction.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.success = null;
        state.projectOverviewUpdateTask = null;
      })
      .addCase(dmProjectsAction.updateProjectTaskAction.fulfilled, (state, action) => {
            state.loading = false;
            if(action?.payload?.data?.status===200){
              state.error = null;
              state.success = action?.payload?.data?.message;
              state.projectOverviewUpdateTask = action?.payload?.data?.data;
            }else{
              state.success = null;
              state.projectOverviewUpdateTask = null;
              state.error = 'Projects task could not be updated.';
            }
      })
      .addCase(dmProjectsAction.updateProjectTaskAction.rejected, (state, action:any) => {
            state.loading = false;
            state.success = null;
            state.projectOverviewUpdateTask = null;
            state.error = action?.payload?.data?.message ?? 'Projects task could not be updated.';
      })

      // -------- cases of delete project task api data -------
      .addCase(dmProjectsAction.deleteProjectTaskAction.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.success = null;
        state.projectOverviewDeleteTask = null;
      })
      .addCase(dmProjectsAction.deleteProjectTaskAction.fulfilled, (state, action) => {
            state.loading = false;
            if(action?.payload?.data?.status===200){
              state.error = null;
              state.success = action?.payload?.data?.message;
              state.projectOverviewDeleteTask = action?.payload?.data?.data;
            }else{
              state.success = null;
              state.projectOverviewDeleteTask = null;
              state.error = 'Projects task could not be deleted.';
            }
      })
      .addCase(dmProjectsAction.deleteProjectTaskAction.rejected, (state, action:any) => {
            state.loading = false;
            state.success = null;
            state.projectOverviewDeleteTask = null;
            state.error = action?.payload?.data?.message ?? 'Projects task could not be deleted.';
      })

      // -------- cases of project timeline task start dateupdate api data -------
      .addCase(dmProjectsAction.inlineUpdateProjectTaskAction.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.success = null;
        // set this as null only when user is not fetching this data while updating project tasks
        if(!action?.meta?.arg?.hideLoader){
          state.projectTimelineList = [];
        }
      })
      .addCase(dmProjectsAction.inlineUpdateProjectTaskAction.fulfilled, (state, action) => {
            state.loading = false;
            if(action?.payload?.data?.status===200){
              state.error = null;
              state.success = action?.payload?.data?.message;
              state.projectTimelineList = action?.payload?.data?.data?.map((item:any, index:number)=>{
                return {
                  ...item,
                  menuPosition: action?.payload?.data?.data?.length<4 || action?.payload?.data?.data?.length-index>3?true:false,
                }
              });
            }else{
              state.success = null;
              state.projectTimelineList = [];
              state.error = 'Projects timeline list could not be fetched.';
            }
      })
      .addCase(dmProjectsAction.inlineUpdateProjectTaskAction.rejected, (state, action:any) => {
            state.loading = false;
            state.success = null;
            state.projectTimelineList = [];
            state.error = action?.payload?.data?.message ?? 'Projects timeline list could not be fetched.';
      })

      // -------- get Change Order -------
      .addCase(dmProjectsAction.getChangeOrderAction.pending, (state, action) => {
        state.error = "";
        state.projectChangeOrderList = [];
      })
      .addCase(dmProjectsAction.getChangeOrderAction.fulfilled, (state, action) => {
        if (action?.payload?.data?.status===200) {
          state.projectChangeOrderList= action?.payload?.data?.data;
          state.error = "";
        } else {
          state.error = "Failed to fetch change orders.";
        }
      })
      .addCase(dmProjectsAction.getChangeOrderAction.rejected, (state, action:any) => {
        state.projectChangeOrderList = [];
        state.error = action?.payload?.data?.message || "Failed to fetch change orders.";
      })

      // -------- get Change Order items -------
      .addCase(dmProjectsAction.getChangeOrderItemsAction.pending, (state, action) => {
        state.error = "";
        state.projectChangeOrderItems = [];
      })
      .addCase(dmProjectsAction.getChangeOrderItemsAction.fulfilled, (state, action) => {
        if (action?.payload?.data?.status===200) {
          state.projectChangeOrderItems= action?.payload?.data?.data;
          state.error = "";
        } else {
          state.error = "Failed to fetch change orders items.";
        }
      })
      .addCase(dmProjectsAction.getChangeOrderItemsAction.rejected, (state, action:any) => {
        state.projectChangeOrderItems = [];
        state.error = action?.payload?.data?.message || "Failed to fetch change orders items.";
      })

      // -------- create Change Order -------
      .addCase(dmProjectsAction.createChangeOrderAction.pending, (state, action) => {
        state.error = "";
        state.createChangeOrder = null;
      })
      .addCase(dmProjectsAction.createChangeOrderAction.fulfilled, (state, action) => {
        if (action?.payload?.data?.status===200) {
          state.createChangeOrder= action?.payload?.data?.data;
          state.error = "";
        } else {
          state.error = "Failed to create change order.";
        }
      })
      .addCase(dmProjectsAction.createChangeOrderAction.rejected, (state, action:any) => {
        state.createChangeOrder = null;
        state.error = action?.payload?.data?.message || "Failed to create change order.";
      })

      // -------- save Change Order -------
      .addCase(dmProjectsAction.saveChangeOrderAction.pending, (state, action) => {
        state.error = "";
        // state.createChangeOrder = null;
      })
      .addCase(dmProjectsAction.saveChangeOrderAction.fulfilled, (state, action) => {
        if (action?.payload?.data?.status===200) {
          // state.createChangeOrder= action?.payload?.data?.data;
          state.error = "";
        } else {
          state.error = "Failed to save change order.";
        }
      })
      .addCase(dmProjectsAction.saveChangeOrderAction.rejected, (state, action:any) => {
        // state.createChangeOrder = null;
        state.error = action?.payload?.data?.message || "Failed to save change order.";
      })

      // -------- get Change Order Details -------
      .addCase(dmProjectsAction.getChangeOrderDetailsAction.pending, (state, action) => {
        state.error = "";
        state.changeOrderDetails = null;
      })
      .addCase(dmProjectsAction.getChangeOrderDetailsAction.fulfilled, (state, action) => {
        if (action?.payload?.data?.status===200) {
          state.changeOrderDetails= action?.payload?.data?.data;
          state.error = "";
        } else {
          state.error = "Failed to get change order details.";
        }
      })
      .addCase(dmProjectsAction.getChangeOrderDetailsAction.rejected, (state, action:any) => {
        state.changeOrderDetails = null;
        state.error = action?.payload?.data?.message || "Failed to get change order details.";
      })

      // -------- get Change Order terms -------
      .addCase(dmProjectsAction.getChangeOrderTermsAction.pending, (state, action) => {
        state.error = "";
        state.changeOrderTerms = null;
      })
      .addCase(dmProjectsAction.getChangeOrderTermsAction.fulfilled, (state, action) => {
        if (action?.payload?.data?.status===200) {
          state.changeOrderTerms= action?.payload?.data?.data;
          state.error = "";
        } else {
          state.error = "Failed to get change order terms.";
        }
      })
      .addCase(dmProjectsAction.getChangeOrderTermsAction.rejected, (state, action:any) => {
        state.changeOrderTerms = null;
        state.error = action?.payload?.data?.message || "Failed to get change order terms.";
      })

      // -------- save Change Order notes -------
      .addCase(dmProjectsAction.saveChangeOrderNotesAction.pending, (state, action) => {
        state.error = "";
        state.savedChangeOrderNotes = null;
      })
      .addCase(dmProjectsAction.saveChangeOrderNotesAction.fulfilled, (state, action) => {
        if (action?.payload?.data?.status===200) {
          state.savedChangeOrderNotes= action?.payload?.data?.data;
          state.error = "";
        } else {
          state.error = "Failed to save change order notes.";
        }
      })
      .addCase(dmProjectsAction.saveChangeOrderNotesAction.rejected, (state, action:any) => {
        state.savedChangeOrderNotes = null;
        state.error = action?.payload?.data?.message || "Failed to save change order notes.";
      })

      // -------- save Change Order Intro -------
      .addCase(dmProjectsAction.saveChangeOrderIntroAction.pending, (state, action) => {
        state.error = "";
        // state.changeOrderTerms = null;
      })
      .addCase(dmProjectsAction.saveChangeOrderIntroAction.fulfilled, (state, action) => {
        if (action?.payload?.data?.status===200) {
          // state.changeOrderTerms= action?.payload?.data?.data;
          state.error = "";
        } else {
          state.error = "Failed to save change order intro.";
        }
      })
      .addCase(dmProjectsAction.saveChangeOrderIntroAction.rejected, (state, action:any) => {
        // state.changeOrderTerms = null;
        state.error = action?.payload?.data?.message || "Failed to save change order intro.";
      })

      // -------- upload Change Order pdf -------
      .addCase(dmProjectsAction.uploadChangeOrderPdfAction.pending, (state, action) => {
        state.changeOrderUploadSuccess = false;
        state.error = "";
      })
      .addCase(dmProjectsAction.uploadChangeOrderPdfAction.fulfilled, (state, action) => {
        if (action?.payload?.data?.status===200) {
          state.changeOrderUploadSuccess = true;
          state.error = "";
        } else {
          state.changeOrderUploadSuccess = false;
          state.error = "Failed to upload change order pdf.";
        }
      })
      .addCase(dmProjectsAction.uploadChangeOrderPdfAction.rejected, (state, action:any) => {
        state.changeOrderUploadSuccess = false;
        state.error = action?.payload?.data?.message || "Failed to upload change order pdf.";
      })

      // -------- get uploaded document list -------
      .addCase(dmProjectsAction.changeOrderAttachmentListAction.pending, (state, action) => {
        state.error = "";
        state.changeOrderAttachmentList = null;
      })
      .addCase(dmProjectsAction.changeOrderAttachmentListAction.fulfilled, (state, action) => {
        if (action?.payload?.data?.status===200) {
          state.changeOrderAttachmentList= action?.payload?.data?.data;
          state.error = "";
        } else {
          state.error = "Failed to get uploaded document list.";
        }
      })
      .addCase(dmProjectsAction.changeOrderAttachmentListAction.rejected, (state, action:any) => {
        state.changeOrderAttachmentList = null;
        state.error = action?.payload?.data?.message || "Failed to get uploaded document list.";
      })

      // -------- remove uploaded attachment -------
      .addCase(dmProjectsAction.removeChangeOrderAttachmentAction.pending, (state, action) => {
        state.changeOrderRemoveSuccess = false;
        state.error = "";
      })
      .addCase(dmProjectsAction.removeChangeOrderAttachmentAction.fulfilled, (state, action) => {
        if (action?.payload?.data?.status===200) {
          state.changeOrderRemoveSuccess = true;
          state.error = "";
        } else {
          state.changeOrderRemoveSuccess = false;
          state.error = "Failed to remove uploaded attachment.";
        }
      })
      .addCase(dmProjectsAction.removeChangeOrderAttachmentAction.rejected, (state, action:any) => {
        state.changeOrderRemoveSuccess = false;
        state.error = action?.payload?.data?.message || "Failed to remove uploaded attachment.";
      })

      // -------- export change order -------
      .addCase(dmProjectsAction.exportChangeOrderAction.pending, (state, action) => {
        state.changeOrderRelease = false;
        state.error = "";
      })
      .addCase(dmProjectsAction.exportChangeOrderAction.fulfilled, (state, action) => {
        if (action?.payload?.data?.status===200) {
          state.changeOrderRelease = true;
          state.error = "";
        } else {
          state.changeOrderRelease = false;
          state.error = "Failed to export change order.";
        }
      })
      .addCase(dmProjectsAction.exportChangeOrderAction.rejected, (state, action:any) => {
        state.changeOrderRelease = false;
        state.error = action?.payload?.data?.message || "Failed to export change order.";
      })

      // -------- delete change order -------
      .addCase(dmProjectsAction.deleteChangeOrderAction.pending, (state, action) => {
        state.changeOrderDelete = null;
        state.error = "";
      })
      .addCase(dmProjectsAction.deleteChangeOrderAction.fulfilled, (state, action) => {
        if (action?.payload?.data?.status===200) {
          state.changeOrderDelete = action?.payload?.data?.data;
          state.error = "";
        } else {
          state.changeOrderDelete = null;
          state.error = "Failed to delete change order.";
        }
      })
      .addCase(dmProjectsAction.deleteChangeOrderAction.rejected, (state, action:any) => {
        state.changeOrderDelete = null;
        state.error = action?.payload?.data?.message || "Failed to delete change order.";
      })

      // -------- duplicate change order -------
      .addCase(dmProjectsAction.duplicateChangeOrderAction.pending, (state, action) => {
        state.changeOrderDuplicate = null;
        state.error = "";
      })
      .addCase(dmProjectsAction.duplicateChangeOrderAction.fulfilled, (state, action) => {
        if (action?.payload?.data?.status===200) {
          state.changeOrderDuplicate = action?.payload?.data?.data;
          state.error = "";
        } else {
          state.changeOrderDuplicate = null;
          state.error = "Failed to create duplicate change order.";
        }
      })
      .addCase(dmProjectsAction.duplicateChangeOrderAction.rejected, (state, action:any) => {
        state.changeOrderDuplicate = null;
        state.error = action?.payload?.data?.message || "Failed to create duplicate change order.";
      })

      // get payment schedule
      .addCase(dmProjectsAction.getCOPaymentScheduleAction.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
        state.coPaymentSchedule = [];
      })
      .addCase(dmProjectsAction.getCOPaymentScheduleAction.fulfilled, (state, action) => {
        state.loading = false;
        if(action?.payload?.data?.status===200){
          state.coPaymentSchedule = action.payload.data.data;
          state.success = action.payload.data.data;
        }else{
          state.success = null;
          state.coPaymentSchedule = [];
          state.error = action?.payload?.data ?? 'Get Payment Failed';
        }
      })
      .addCase(dmProjectsAction.getCOPaymentScheduleAction.rejected, (state, action:any) => {
        state.loading = false;
        state.success = null;
        state.coPaymentSchedule = [];
        state.error = action?.payload?.data ?? 'Get Payment Failed';
      })

      // -------- rfp list -------
      .addCase(dmProjectsAction.rfpListAction.pending, (state, action) => {
        state.rfpList = null;
        state.error = "";
      })
      .addCase(dmProjectsAction.rfpListAction.fulfilled, (state, action) => {
        if (action?.payload?.data?.status === 200) {
          state.rfpList = action?.payload?.data?.data;
          state.error = "";
        } else {
          state.rfpList = null;
          state.error = "Failed to fetch rfp list.";
        }
      })
      .addCase(dmProjectsAction.rfpListAction.rejected, (state, action: any) => {
        state.rfpList = null;
        state.error = action?.payload?.data?.message || "Failed to fetch rfp list.";
      })

      // -------- create rfp -------
      .addCase(dmProjectsAction.createRfpAction.pending, (state, action) => {
        // state.rfpList = null;
        state.error = "";
      })
      .addCase(dmProjectsAction.createRfpAction.fulfilled, (state, action) => {
        if (action?.payload?.data?.status === 200) {
          // state.rfpList = action?.payload?.data?.data;
          state.error = "";
        } else {
          // state.rfpList = null;
          state.error = "Failed to create rfp.";
        }
      })
      .addCase(dmProjectsAction.createRfpAction.rejected, (state, action: any) => {
        // state.rfpList = null;
        state.error = action?.payload?.data?.message || "Failed to create rfp.";
      })

      // -------- update rfp -------
      .addCase(dmProjectsAction.updateRfpAction.pending, (state, action) => {
        // state.rfpList = null;
        state.error = "";
      })
      .addCase(dmProjectsAction.updateRfpAction.fulfilled, (state, action) => {
        if (action?.payload?.data?.status === 200) {
          // state.rfpList = action?.payload?.data?.data;
          state.error = "";
        } else {
          // state.rfpList = null;
          state.error = "Failed to update rfp.";
        }
      })
      .addCase(dmProjectsAction.updateRfpAction.rejected, (state, action: any) => {
        // state.rfpList = null;
        state.error = action?.payload?.data?.message || "Failed to update rfp.";
      })

      // -------- unrelease change order -------
      .addCase(dmProjectsAction.unreleaseChangeOrderAction.pending, (state, action) => {
        state.error = "";
      })
      .addCase(dmProjectsAction.unreleaseChangeOrderAction.fulfilled, (state, action) => {
        if (action?.payload?.data?.status === 200) {
          state.error = "";
        } else {
          state.error = "Failed to unrelease change order.";
        }
      })
      .addCase(dmProjectsAction.unreleaseChangeOrderAction.rejected, (state, action: any) => {
        state.error = action?.payload?.data?.message || "Failed to unrelease change order.";
      })

      // -------- rearrange overview list -------
      .addCase(dmProjectsAction.rearrangeOverviewListAction.pending, (state, action) => {
        state.error = "";
      })
      .addCase(dmProjectsAction.rearrangeOverviewListAction.fulfilled, (state, action) => {
        if (action?.payload?.data?.status === 200) {
          state.error = "";
        } else {
          state.error = "Failed to rearrange overview list.";
        }
      })
      .addCase(dmProjectsAction.rearrangeOverviewListAction.rejected, (state, action: any) => {
        state.error = action?.payload?.data?.message || "Failed to rearrange overview list.";
      })

      // -------- generate zip -------
      .addCase(dmProjectsAction.generateZipAction.pending, (state, action) => {
        state.error = "";
      })
      .addCase(dmProjectsAction.generateZipAction.fulfilled, (state, action) => {
        if (action?.payload?.data?.status === 200) {
          state.error = "";
        } else {
          state.error = "Failed to generate zip.";
        }
      })
      .addCase(dmProjectsAction.generateZipAction.rejected, (state, action: any) => {
        state.error = action?.payload?.data?.message || "Failed to generate zip.";
      })

  },
});

export const { cleanChangeOrder, setProjectTimelineList } = dmProjectsSlice.actions;
export default dmProjectsSlice.reducer;